import {
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'preact/hooks';
import './MenuButton.less';
import Button, { ButtonProps } from './Button';
import type { PropsWithChildren } from 'preact/compat';
import { computePosition, autoUpdate } from '@floating-ui/dom';
import OutsideFocusHandler from '../utils/OutsideFocusHandler';
import { ComponentChild, Ref } from 'preact';

export type MenuButtonProps = PropsWithChildren<{
    className?: string;
    menuContent: ComponentChild;
    isActive?: boolean;
    actions?: MenuButtonActions;
}> &
    Pick<ButtonProps, 'disabled'>;
export type MenuButtonActions = Ref<{ close: () => void }>;

const MenuButton = ({
    className = '',
    children,
    menuContent,
    isActive,
    actions = () => undefined,
    disabled
}: MenuButtonProps) => {
    const [isOpen, setOpen] = useState(false);
    useImperativeHandle(actions, () => {
        return {
            close: () => setOpen(false)
        };
    });
    const anchorRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const onAnchorClick = useCallback(() => {
        setOpen(!isOpen);
    }, [isOpen]);

    const onFocusOutside = useCallback(
        (event: MouseEvent | TouchEvent | FocusEvent) => {
            if (!anchorRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        },
        []
    );

    const updatePosition = useCallback(async () => {
        if (!isOpen || !anchorRef.current || !menuRef.current) {
            return;
        }
        const { x, y } = await computePosition(
            anchorRef.current,
            menuRef.current,
            { placement: 'bottom-start' }
        );
        setPosition({ top: y, left: x });
    }, [anchorRef.current, menuRef.current, isOpen]);

    useEffect(() => {
        if (isOpen && anchorRef.current && menuRef.current) {
            return autoUpdate(
                anchorRef.current,
                menuRef.current,
                updatePosition
            );
        }
    }, [anchorRef.current, menuRef.current, updatePosition, isOpen]);

    return (
        <>
            <Button
                disabled={disabled}
                variant="tertiary"
                endIconId="chevron-down"
                className={`bri-MenuButton ${className} ${
                    isOpen ? 'bri-visible' : ''
                }`}
                size="medium"
                onClick={onAnchorClick}
                ref={anchorRef}
                active={isOpen || isActive}
            >
                {children}
            </Button>
            <div
                className={`bri-MenuButton-popover ${className} ${
                    isOpen ? 'bri-visible' : ''
                }`}
                ref={menuRef}
                style={position}
            >
                <OutsideFocusHandler
                    onClickOutside={onFocusOutside}
                    onFocusOutside={onFocusOutside}
                >
                    {menuContent}
                </OutsideFocusHandler>
            </div>
        </>
    );
};

export default MenuButton;
